import { useEffect, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Typography } from '@interstate/components/Typography';
import type { ContractFormType } from '@makemydeal/dr-dash-types';
import {
    ContractFormTypeBodyGroup,
    ContractFormTypeModalContainer,
    ContractFormTypeOptionRow,
    ContractFormTypeOptionsBox,
    ContractFormTypeSelectionGroup
} from './ContractFormTypeModal.style';

export interface ContractFormTypeModalProps {
    show: boolean;
    defaultFormType?: ContractFormType;
    onConfirm: (formType: ContractFormType) => void;
    onClose: () => void;
}

const CONTRACT_FORM_TYPE_MODAL_TEST_ID = 'contract-form-type-modal';

const FORM_TYPE_OPTIONS: Array<{ value: ContractFormType; title: string; testId: string }> = [
    { value: 'ARB', title: 'ARB', testId: `${CONTRACT_FORM_TYPE_MODAL_TEST_ID}-option-arb` },
    { value: 'NARB', title: 'Non-ARB', testId: `${CONTRACT_FORM_TYPE_MODAL_TEST_ID}-option-non-arb` }
];

const isSelectionKey = (key: string): boolean => key === 'Enter' || key === ' ';

const ContractFormTypeModal = ({ show, defaultFormType = 'ARB', onConfirm, onClose }: ContractFormTypeModalProps) => {
    const [selectedFormType, setSelectedFormType] = useState<ContractFormType>(defaultFormType);

    useEffect(() => {
        if (show) {
            setSelectedFormType(defaultFormType);
        }
    }, [show, defaultFormType]);

    return (
        <ContractFormTypeModalContainer
            show={show}
            onHide={onClose}
            size="large"
            id={CONTRACT_FORM_TYPE_MODAL_TEST_ID}
            data-testid={CONTRACT_FORM_TYPE_MODAL_TEST_ID}
            header={<Typography variant="h3">Contract Form Type</Typography>}
            footer={{
                options: [
                    {
                        action: onClose,
                        label: 'Cancel',
                        buttonStyle: 'tertiary',
                        'data-testid': `${CONTRACT_FORM_TYPE_MODAL_TEST_ID}-cancel-button`
                    }
                ],
                primary: {
                    action: () => onConfirm(selectedFormType),
                    label: 'Proceed',
                    'data-testid': `${CONTRACT_FORM_TYPE_MODAL_TEST_ID}-proceed-button`
                }
            }}
        >
            <ContractFormTypeBodyGroup>
                <Typography variant="body-sm">
                    You are about to generate a paper contract for an external lender.{' '}
                    <Typography variant="strong-sm" tag="span">
                        Proceeding will cancel all existing contracts.
                    </Typography>
                </Typography>
                <Typography variant="body-sm">ARB is selected by default - you can change this before proceeding.</Typography>
            </ContractFormTypeBodyGroup>
            <ContractFormTypeSelectionGroup>
                <Typography variant="h6" tag="div">
                    Select Form Type
                </Typography>
                <ContractFormTypeOptionsBox role="radiogroup" aria-label="Select Form Type">
                    {FORM_TYPE_OPTIONS.map((option) => {
                        const isSelected = selectedFormType === option.value;

                        return (
                            <ContractFormTypeOptionRow
                                key={option.value}
                                className={isSelected ? 'selectedOptionRow' : undefined}
                                onClick={() => setSelectedFormType(option.value)}
                                onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                                    if (isSelectionKey(event.key)) {
                                        event.preventDefault();
                                        setSelectedFormType(option.value);
                                    }
                                }}
                                data-testid={option.testId}
                                role="radio"
                                aria-checked={isSelected}
                                tabIndex={0}
                            >
                                <Typography variant="h6" tag="div">
                                    {option.title}
                                </Typography>
                            </ContractFormTypeOptionRow>
                        );
                    })}
                </ContractFormTypeOptionsBox>
            </ContractFormTypeSelectionGroup>
        </ContractFormTypeModalContainer>
    );
};

export default ContractFormTypeModal;
